import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

export type SignaturePadHandle = { toDataURL: () => string | null; clear: () => void; isEmpty: () => boolean };

type Point = { x: number; y: number };

const CURSIVE = '"Snell Roundhand", "Segoe Script", "Brush Script MT", "Dancing Script", cursive';

/**
 * Firma del cliente — A PRUEBA DE MÓVIL (los clientes firman desde el teléfono):
 *  - Dos modos: DIBUJAR o ESCRIBIR el nombre (firma tipográfica, E-SIGN válida —
 *    la spec original ya pedía "dibuja/escribe"). Si el dedo falla, escribir salva.
 *  - Los trazos viven en un MODELO (array de strokes), no solo en los píxeles: el
 *    canvas se redibuja tras resize/rotación y el PNG se exporta re-renderizando el
 *    modelo en un canvas nuevo — sobrevive a los "clears" silenciosos de iOS cuando
 *    hay presión de memoria (PDF de varias páginas arriba).
 *  - Pointer events con fallback a touch+mouse (webviews de apps de correo viejas),
 *    touch listeners NO pasivos (permite preventDefault → no scrollea al firmar).
 */
export const SignaturePad = forwardRef<SignaturePadHandle, { height?: number }>(function SignaturePad({ height = 180 }, ref) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const strokes = useRef<Point[][]>([]);
  const drawing = useRef(false);
  const [mode, setMode] = useState<'draw' | 'type'>('draw');
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const [typed, setTyped] = useState('');
  const typedRef = useRef(typed);
  typedRef.current = typed;

  function cssSize(): { w: number; h: number } {
    const c = canvasRef.current;
    const w = c ? Math.max(1, c.getBoundingClientRect().width) : 300;
    return { w, h: height };
  }

  /** Redibuja TODO el estado (trazos o nombre) en un canvas dado. */
  function paint(c: HTMLCanvasElement, w: number, h: number, scale: number) {
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    ctx.clearRect(0, 0, w, h);
    ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827'; ctx.fillStyle = '#111827';
    if (modeRef.current === 'type') {
      const name = typedRef.current.trim();
      if (!name) return;
      let size = Math.min(64, h * 0.45);
      ctx.font = `${size}px ${CURSIVE}`;
      while (size > 16 && ctx.measureText(name).width > w - 32) { size -= 2; ctx.font = `${size}px ${CURSIVE}`; }
      ctx.textBaseline = 'middle';
      ctx.fillText(name, (w - ctx.measureText(name).width) / 2, h / 2);
      return;
    }
    for (const st of strokes.current) {
      if (!st.length) continue;
      ctx.beginPath();
      ctx.moveTo(st[0]!.x, st[0]!.y);
      if (st.length === 1) ctx.lineTo(st[0]!.x + 0.1, st[0]!.y + 0.1); // un tap deja punto
      for (let i = 1; i < st.length; i++) ctx.lineTo(st[i]!.x, st[i]!.y);
      ctx.stroke();
    }
  }

  function repaintVisible() {
    const c = canvasRef.current; if (!c) return;
    const { w, h } = cssSize();
    const dpr = window.devicePixelRatio || 1;
    if (c.width !== Math.round(w * dpr) || c.height !== Math.round(h * dpr)) {
      c.width = Math.round(w * dpr); c.height = Math.round(h * dpr);
    }
    paint(c, w, h, dpr);
  }

  // Redibuja tras el layout inicial, en resize/rotación, y cuando cambia modo/nombre.
  useEffect(() => {
    repaintVisible();
    const c = canvasRef.current; if (!c) return;
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => repaintVisible()) : null;
    ro?.observe(c);
    const onResize = () => repaintVisible();
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => { ro?.disconnect(); window.removeEventListener('resize', onResize); window.removeEventListener('orientationchange', onResize); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, typed, height]);

  // Input de dibujo: pointer events si existen; si no, touch + mouse. Se registran a
  // mano (addEventListener) para poder pasar { passive: false } en touch — sin eso,
  // algunos webviews scrollean la página en vez de dibujar.
  useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    const toPoint = (clientX: number, clientY: number): Point => {
      const r = c.getBoundingClientRect();
      return { x: clientX - r.left, y: clientY - r.top };
    };
    const start = (x: number, y: number) => {
      if (modeRef.current !== 'draw') return;
      drawing.current = true;
      strokes.current.push([toPoint(x, y)]);
      repaintVisible();
    };
    const move = (x: number, y: number) => {
      if (!drawing.current || modeRef.current !== 'draw') return;
      strokes.current[strokes.current.length - 1]!.push(toPoint(x, y));
      repaintVisible();
    };
    const end = () => { drawing.current = false; };

    if (typeof window !== 'undefined' && 'PointerEvent' in window) {
      const pd = (e: PointerEvent) => { e.preventDefault(); try { c.setPointerCapture(e.pointerId); } catch { /* webviews viejos */ } start(e.clientX, e.clientY); };
      const pm = (e: PointerEvent) => { if (drawing.current) e.preventDefault(); move(e.clientX, e.clientY); };
      c.addEventListener('pointerdown', pd);
      c.addEventListener('pointermove', pm);
      c.addEventListener('pointerup', end);
      c.addEventListener('pointercancel', end);
      c.addEventListener('pointerleave', end);
      return () => { c.removeEventListener('pointerdown', pd); c.removeEventListener('pointermove', pm); c.removeEventListener('pointerup', end); c.removeEventListener('pointercancel', end); c.removeEventListener('pointerleave', end); };
    }
    // Fallback sin pointer events (iOS/webviews muy viejos): touch (no pasivo) + mouse.
    const ts = (e: TouchEvent) => { e.preventDefault(); const t = e.touches[0]; if (t) start(t.clientX, t.clientY); };
    const tm = (e: TouchEvent) => { e.preventDefault(); const t = e.touches[0]; if (t) move(t.clientX, t.clientY); };
    const md = (e: MouseEvent) => { e.preventDefault(); start(e.clientX, e.clientY); };
    const mm = (e: MouseEvent) => move(e.clientX, e.clientY);
    c.addEventListener('touchstart', ts, { passive: false });
    c.addEventListener('touchmove', tm, { passive: false });
    c.addEventListener('touchend', end);
    c.addEventListener('mousedown', md);
    c.addEventListener('mousemove', mm);
    c.addEventListener('mouseup', end);
    c.addEventListener('mouseleave', end);
    return () => {
      c.removeEventListener('touchstart', ts); c.removeEventListener('touchmove', tm); c.removeEventListener('touchend', end);
      c.removeEventListener('mousedown', md); c.removeEventListener('mousemove', mm); c.removeEventListener('mouseup', end); c.removeEventListener('mouseleave', end);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function clear() {
    strokes.current = [];
    setTyped('');
    repaintVisible();
  }

  // Tinta MÍNIMA: un tap accidental (1 punto) no cuenta como firma — se exige un trazo
  // real (≥8 puntos en total). En modo escribir, al menos 2 caracteres.
  const isEmpty = () => (mode === 'type'
    ? typed.trim().length < 2
    : strokes.current.reduce((n, s) => n + s.length, 0) < 8);

  useImperativeHandle(ref, () => ({
    // Exporta RE-RENDERIZANDO el modelo en un canvas nuevo a 2x — no depende de los
    // píxeles del canvas visible (iOS puede haberlo limpiado por memoria).
    toDataURL: () => {
      if (isEmpty()) return null;
      const { w, h } = cssSize();
      const out = document.createElement('canvas');
      out.width = Math.round(w * 2); out.height = Math.round(h * 2);
      paint(out, w, h, 2);
      return out.toDataURL('image/png');
    },
    clear,
    isEmpty,
  }));

  const tabBtn = (m: 'draw' | 'type', label: string) => (
    <button
      type="button"
      onClick={() => setMode(m)}
      style={{
        flex: 1, padding: '9px 10px', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'], cursor: 'pointer', borderRadius: 8,
        border: mode === m ? '2px solid var(--red)' : '1px solid var(--line)',
        background: mode === m ? '#fff' : 'var(--surface-2)', color: mode === m ? 'var(--red)' : '#374151',
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <div style={{ display: 'flex', gap: 8 }}>
        {tabBtn('draw', 'Draw · Dibujar')}
        {tabBtn('type', 'Type · Escribir')}
      </div>
      {mode === 'type' && (
        <input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder="Type your full name · Escribe tu nombre"
          style={{ padding: '10px 12px', border: '1px solid var(--line)', borderRadius: 8, fontSize: 'var(--fs-base)', background: '#fff' }}
        />
      )}
      <canvas
        ref={canvasRef} height={height}
        style={{ width: '100%', height, background: '#fff', border: '1px dashed #9ca3af', borderRadius: 10, touchAction: 'none', cursor: mode === 'draw' ? 'crosshair' : 'default', display: 'block', WebkitUserSelect: 'none', userSelect: 'none' }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--fs-2xs)', color: 'var(--muted)' }}>
        <span>{mode === 'draw' ? 'Draw your signature above · Dibuja tu firma arriba' : 'Preview of your signature · Así se verá tu firma'}</span>
        <button type="button" className="btn ghost sm" onClick={clear}>Clear</button>
      </div>
    </div>
  );
});
